import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Support() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.first_name || "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined })); };

  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  const phoneValid = (v) => {
    const s = v.trim();
    const digits = s.replace(/\D/g, "");
    return /^\+?[\d\s().-]+$/.test(s) && digits.length >= 7 && digits.length <= 15;
  };
  const errText = (code) => (code === "invalid" ? t("phone_invalid") : t("field_required"));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "required";
    if (!form.phone.trim()) e.phone = "required";
    else if (!phoneValid(form.phone)) e.phone = "invalid";
    if (!form.message.trim()) e.message = "required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const sendCallback = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/support/callback", { name: form.name, phone: form.phone, message: form.message, topic: "callback" });
      setSent(true); toast.success(t("support_sent"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail, t("err_generic"))); }
    finally { setBusy(false); }
  };

  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true); setAnswer("");
    try {
      const { data } = await api.post("/assist", { question: q, lang });
      setAnswer(data.answer);
    } catch (e) { toast.error(t("err_generic")); }
    finally { setAsking(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-head font-black text-4xl text-center mb-3">🤝 {t("support_title")}</h1>
      <p className="text-center text-stone-600 text-lg mb-10 max-w-2xl mx-auto">{t("support_sub")}</p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Callback */}
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-7" data-testid="callback-form">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center"><Phone size={22} /></span>
            <h2 className="font-head font-bold text-xl">{t("support_callback")}</h2>
          </div>
          {sent ? (
            <div className="text-center py-10">
              <div className="text-5xl mb-4">😊</div>
              <p className="font-semibold text-stone-700">{t("support_sent")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div><Label className="font-semibold">{t("support_name")}</Label>
                <Input value={form.name} onChange={set("name")} data-testid="callback-name" className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1.5" />
                {errors.name && <p className="text-sm text-red-500 mt-1" data-testid="error-name">{errText(errors.name)}</p>}</div>
              <div><Label className="font-semibold">{t("support_phone")}</Label>
                <Input value={form.phone} onChange={set("phone")} type="tel" inputMode="tel" data-testid="callback-phone" className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1.5" />
                {errors.phone && <p className="text-sm text-red-500 mt-1" data-testid="error-phone">{errText(errors.phone)}</p>}</div>
              <div><Label className="font-semibold">{t("support_msg")}</Label>
                <Textarea value={form.message} onChange={set("message")} placeholder={t("support_msg_ph")} data-testid="callback-message" className="rounded-xl border-2 border-stone-300 mt-1.5 min-h-[90px]" />
                {errors.message && <p className="text-sm text-red-500 mt-1" data-testid="error-message">{errText(errors.message)}</p>}</div>
              <Button onClick={sendCallback} disabled={busy} data-testid="callback-submit" className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[54px] font-bold">
                {busy ? <Loader2 className="animate-spin" /> : t("support_send")}
              </Button>
            </div>
          )}
        </div>

        {/* AI assistant */}
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-7" data-testid="assist-panel">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-12 rounded-2xl bg-accent/10 text-accent grid place-items-center"><MessageCircle size={22} /></span>
            <h2 className="font-head font-bold text-xl">{t("support_ai_title")}</h2>
          </div>
          <Textarea value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("support_ask_ph")} data-testid="assist-input"
            className="rounded-xl border-2 border-stone-300 min-h-[100px]" />
          <Button onClick={ask} disabled={asking || !q.trim()} data-testid="assist-submit" className="w-full rounded-full bt-gradient text-white hover:opacity-90 min-h-[54px] font-bold mt-4">
            {asking ? <Loader2 className="animate-spin" /> : <><Send size={17} className="mr-2" /> {t("support_ask")}</>}
          </Button>
          {answer && (
            <div className="mt-5 p-4 rounded-2xl bg-secondary/60 text-stone-800 leading-relaxed bt-rise" data-testid="assist-answer">
              {answer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
